import NumberPicker from '@/components/NumberPicker'
import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'
import { mobileMediaQuery } from '@caterdesk/ui--theme'
import { TextExtraSmall } from '@caterdesk/ui--typography'
import { amountToPriceString, Currency } from '@caterdesk/utils--money'
import { DietaryRow } from '@fusion/dietary/row'
import { Box, Stack } from '@mui/material'

import React from 'react'
import { useMedia } from 'react-use'

import type { PriceAudience } from '../../domain/menu-item'
import { MenuItemOption, MenuOptionItem } from '../../domain/menu-item-option'
import { SelectedOptionItem } from './menu-item-modal'

type Props = {
  option: MenuItemOption
  currency: Currency
  quantity: number
  selectedOptionItems?: Map<string, SelectedOptionItem>
  selectOptionItem: (option: MenuItemOption, item: MenuOptionItem, quantity: number) => void
  pricesIncTax?: boolean
  priceAudience?: PriceAudience
  showPriceBasisPerItem?: boolean
  taxAcronym: string
}

export const NumberOptions: React.FC<Props> = ({
  option,
  currency,
  quantity,
  selectedOptionItems,
  selectOptionItem,
  pricesIncTax,
  priceAudience,
  showPriceBasisPerItem,
  taxAcronym,
}) => {
  const isMobileScreen = useMedia(mobileMediaQuery, false)

  const getMaximum = (itemId: string) => {
    const { maxQty, qtyPerPerson } = option

    const getMaxQty = (
      selectedOptionItems: Map<string, SelectedOptionItem>,
      maximumItemsRequired: number,
      currentQty: number,
    ) => {
      const itemQuantities = Array.from(selectedOptionItems.values()).map(
        ({ quantity }) => quantity,
      )
      const numberOfItemsSelected = itemQuantities.reduce(
        (total, itemQty) => total + (itemQty || 0),
        0,
      )

      return numberOfItemsSelected >= maximumItemsRequired ? currentQty : undefined
    }

    if (selectedOptionItems) {
      const currentQty = selectedOptionItems?.get(itemId)?.quantity || 0
      if (qtyPerPerson) {
        const maximumItemsRequired = quantity * qtyPerPerson
        return getMaxQty(selectedOptionItems, maximumItemsRequired, currentQty)
      }
      if (maxQty) {
        if (selectedOptionItems.size === maxQty) {
          return currentQty ? getMaxQty(selectedOptionItems, quantity, currentQty) : 0
        }
        return getMaxQty(selectedOptionItems, quantity, currentQty)
      }
    }
  }

  return (
    <>
      {option.items.map((item) => {
        const priceSource =
          priceAudience === 'vendor'
            ? { price: item.vendorPrice, priceExTax: item.vendorPriceExTax }
            : getCustomerFacingPriceItem(item.customerPriceItems)
        const isIncTaxFallback = !pricesIncTax && priceSource.priceExTax == null
        const priceToShow = pricesIncTax
          ? priceSource.price
          : (priceSource.priceExTax ?? priceSource.price)
        return (
          <Box
            key={item._id}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 'none' },
            }}>
            <Box sx={{ px: isMobileScreen ? 2 : 3, py: 2 }}>
              <TextExtraSmall style={{ lineHeight: '24px', marginBottom: '6px' }}>
                {item.item.name}
              </TextExtraSmall>
              <Stack spacing={1} justifyContent="space-between">
                <Stack spacing={1}>
                  <NumberPicker
                    size="small"
                    border={false}
                    onChange={(value) => selectOptionItem(option, item, value)}
                    value={selectedOptionItems?.get(item._id)?.quantity || 0}
                    max={getMaximum(item._id)}
                  />
                  <DietaryRow maxToShow={3} dietaries={item.item.dietaries} allergens={[]} />
                  <DietaryRow
                    maxToShow={3}
                    dietaries={[]}
                    allergens={item.item.allergens}
                    subAllergens={item.item.subAllergens}
                  />
                </Stack>
                {typeof priceToShow === 'number' && (
                  <TextExtraSmall style={{ textAlign: 'right' }}>
                    {amountToPriceString(currency, priceToShow, true)}
                    {showPriceBasisPerItem &&
                      ` (${isIncTaxFallback || pricesIncTax ? 'inc' : 'ex'} ${taxAcronym})`}
                  </TextExtraSmall>
                )}
              </Stack>
            </Box>
          </Box>
        )
      })}
    </>
  )
}
